// // // // //
// GENERAL
// Basic items about the application.
// // // // //
export const COMPANY_NAME = "Ayn Rand Institute";
export const WEBSITE_NAME = "FRISCO";
export const WEBSITE_SLOGAN = "Flexible Retrieval and Indexing System for Content Optimization";
export const COPYRIGHT = "Copyright © " + COMPANY_NAME;

// // // // //
// TOOLBAR
// Text on top of the page's navigation menu
// // // // //
export const TOOLBAR_TITLE = "FRISCO";
export const TOOLBAR_DESCRIPTION = "Flexible Retrieval and Indexing System for Content Optimization";

// // // // //
// SIDEBAR
// Text on the sidebar on the left side of the page.
// // // // //
export const SIDEBAR_TITLE = `Page selection`;

export const SIDEBAR_OVERVIEW = `Overview`;
export const SIDEBAR_OVERVIEW_TOOLTIP = `General overview of the current state of the app`;

export const SIDEBAR_SEARCH = `Search`;
export const SIDEBAR_SEARCH_TOOLTIP = `Search for resources, transcripts, and quotes, and talk to SLUG, the AI-powered chatbot`;

export const SIDEBAR_ABOUT = `About`;
export const SIDEBAR_ABOUT_TOOLTIP = `View info about this application and how to use it`;

export const SIDEBAR_DATA_CONFIG = `System`;
export const SIDEBAR_ADMINISTRATION = `Management`;

// // // // //
// BUTTONS
// Text on the buttons.
// // // // //
export const BUTTON_SIGNIN = `Sign in`;
export const BUTTON_SIGNUP = `Sign up`;
export const BUTTON_SIGNOUT = `Sign out`;
export const BUTTON_NO_ACCOUNT = `No account? Sign up here!`;
export const BUTTON_ALREADY_HAVE_ACCOUNT = `Already have an account?`;

export const BUTTON_RETURN = `Save & Return`;
export const BUTTON_CANCEL = `Cancel`;
export const BUTTON_BACK = `Back`;
export const BUTTON_NEXT = `Next`;
export const BUTTON_MORE_INFO = `More info`;

// // // // //
// SNACKBAR
// Small message pop-ups.
// // // // //
export const SNACKBAR_SUCCESS_SIGNED_IN = `Signed in!`;
export const SNACKBAR_SUCCESS_SIGNED_UP = `Account successfully created!`;

export const SNACKBAR_ERROR_SESSION_EXPIRED = `Session expired - please log back in!`;
export const SNACKBAR_ERROR_SERVER = `Lost connection to server!`;
export const SNACKBAR_ERROR_RESPONSE = `No response from server`;

export const SNACKBAR_ERROR_FORM_INVALID = `Invalid form, check for any errors!`;
export const SNACKBAR_ERROR_LIBRARY_REQUIRED = `Choose at least one library.`;
export const SNACKBAR_ERROR_VIDEO_TYPE_REQUIRED = `Choose at least one video type, or leave every type selected to save them all.`;
export const SNACKBAR_LIBRARY_WEBHOOK_SENT = `Webhook sent for the selected libraries.`;
export const SNACKBAR_LIBRARY_RECHECK_STARTED = `Channel reread started. New videos will appear as they are saved.`;
export const LIBRARY_WEBHOOK_DIALOG_TITLE = `Send webhook to ARO`;
export const LIBRARY_WEBHOOK_DIALOG_MESSAGE = `Choose the libraries to include. Each selected library is sent in its own webhook. Libraries you leave out are not sent.`;
export const LIBRARY_RECHECK_DIALOG_TITLE = `Reread a YouTube channel`;
export const LIBRARY_RECHECK_DIALOG_MESSAGE = `Choose one library. Frisco will query that channel's full video list and save videos that are not already stored. This does not send the webhook.`;
export const LIBRARY_WEBHOOK_SEND = `Send webhook`;
export const LIBRARY_RECHECK_START = `Reread channel`;
export const LIBRARY_TOOLTIP_WEBHOOK = `Send selected libraries to ARO`;
export const LIBRARY_TOOLTIP_RECHECK = `Reread one YouTube channel`;
export const SEARCH_LIBRARIES_LABEL = `Libraries`;
export const SEARCH_LIBRARIES_HELP = `Leave this empty to search every library. When one or more libraries are selected, SLUG only answers from those libraries.`;
export const LIBRARY_COLUMN_RESOURCES = `Resources`;
export const LIBRARY_COLUMN_INITIALIZED = `Initialized`;
export const LIBRARY_INITIALIZED_YES = `First channel scan finished`;
export const LIBRARY_INITIALIZED_NO = `First channel scan has not finished`;
export const LIBRARY_CONFIG_WEBHOOK = `Send scheduled webhook`;
export const LIBRARY_CONFIG_WEBHOOK_HELP = `After this library finishes a channel sync, send its new videos and playlist changes. The full webhook stays on the send button.`;
export const LIBRARY_CONFIG_CLEANUP = `LLM caption cleanup`;
export const LIBRARY_CONFIG_CLEANUP_HELP = `Transcripts from this library get the second caption pass. Other libraries keep the simple path.`;
export const LIBRARY_CONFIG_CLEANUP_AUTO_ONLY = `Clean auto-generated captions only`;
export const LIBRARY_CONFIG_CLEANUP_AUTO_ONLY_HELP = `When caption cleanup is on, the LLM pass runs only for auto-generated captions and Whisper transcripts. Manual captions stay on the simple path.`;
export const TRANSCRIPT_CAPTION_SOURCE = `Caption source`;
export const TRANSCRIPT_LLM_CLEANED = `LLM cleaned`;
export const TRANSCRIPT_LLM_CLEANED_YES = `Yes`;
export const TRANSCRIPT_LLM_CLEANED_NO = `No`;
export const LIBRARY_CONFIG_VIDEOS = `Save regular videos`;
export const LIBRARY_CONFIG_SHORTS = `Save Shorts`;
export const LIBRARY_CONFIG_STREAMS = `Save livestreams`;
export const LIBRARY_CONFIG_TYPES_HELP = `Leave every type on to save them all. Turn Shorts off to skip YouTube Shorts.`;

export const SNACKBAR_INFO_LOADING = `Loading, please wait...`;

export const SNACKBAR_SUCCESS_CREATED = `New item(s) created!`;
export const SNACKBAR_SUCCESS_UPDATED = `Item(s) updated!`;
export const SNACKBAR_SUCCESS_DELETED = `Item(s) deleted!`;
export const SNACKBAR_SUCCESS_AUTHOR_EXCLUSIONS_NONE = `No linked resources needed updating.`;
export const SNACKBAR_SUCCESS_AUTHOR_EXCLUSIONS_APPLIED = `Removed selected author(s) from resources that only matched excluded phrases. Other authors were left unchanged.`;
export const SNACKBAR_SUCCESS_AUTHOR_ALIASES_NONE = `No matching resources needed updating.`;
export const SNACKBAR_SUCCESS_AUTHOR_ALIASES_APPLIED = `Linked selected author(s) to resources matching their name or aliases. Exclusion phrases were respected. Other authors were left unchanged.`;

export const SNACKBAR_ERROR_NO_CHANGES = `No changes detected!`;
export const SNACKBAR_ERROR_PASSWORD_MATCH = `New password doesn't match!`;

export const SNACKBAR_SUCCESS_HIGHLIGHT_REVIEW = `Review saved!`;

// // // // //
// HOME PAGE: INTRODUCTION
// Initial descriptions.
// // // // //
export const HOME_OVERVIEW_TITLE = `Overview`;
export const HOME_OVERVIEW_SUBTITLE = `General overview of the current state of the app.`;
export const HOME_OVERVIEW_TEXT_1 = `System status:
Version: <version>
Startup: <startup_timestamp:format_date_time>

LLM model: <llm_model>
LLM temperature: <llm_temperature>
Embedding model: <embedding_model>
Embedding dimensions: <embedding_dimensions>
`;

export const HOME_OVERVIEW_NO_DATA_TEXT_1 = `System status:
Version: <version>
Startup: <startup_timestamp:format_date_time>
`;

export const HOME_OVERVIEW_NO_DATA_TEXT_2 = `No data available!

This means the system is not running.

If the issue persists, please contact the administrator.`;

export const HOME_OVERVIEW_NO_DATA_TEXT_3 = `This web application is for interfacing with FRISCO, The Ayn Rand Institute's Flexible Retrieval and Indexing System for Content Optimization.

FRISCO can be used to query, search, and find important transcript sections of media resources like lecture videos. In addition, users can utilize AI to summarize the content, identify key themes, and suggest specific timestamps for video highlights.

Please create an account or sign in to use the system.`;

// // // // //
// HOME PAGE: SEARCH
// Text used in the search page.
// // // // //
export const SEARCH_TITLE = `Search`;
export const SEARCH_SUBTITLE = `Search for resources and transcripts.
There are two seach modes: standard and AI-powered search.
Standard search is a simple keyword-based search, while AI-powered search uses an assistant to answer questions.`;

// // // // //
// ABOUT PAGE
// Text about this application.
// // // // //
export const ABOUT_TITLE = `${WEBSITE_NAME}`;
export const ABOUT_SUBTITLE = `${WEBSITE_SLOGAN}`;

export const ABOUT_TEXT_1 = `This web application is for interfacing with FRISCO, The Ayn Rand Institute's Flexible Retrieval and Indexing System for Content Optimization.

FRISCO can be used to query, search, and find important transcript sections of media resources like lecture videos.

In addition, users can utilize AI to summarize the content, identify key themes, and suggest specific timestamps for video highlights.`;

export const ABOUT_TEXT_2 = `Please refer to the sidebar for more information.

Each page has a tour button in the top right corner, which will guide you through the main features of the page.`;

export const ABOUT_TEXT_3 = `Technical details below:
The frontend is built using React and Material-UI, with single-page application (SPA) architecture.
The backend is built using Python, with the FastAPI framework and SQLAlchemy as the ORM.
The database is PostgreSQL with a few extensions for full-text search and vectors (tsvector, pg_trgm, timescale, pgvectorscale).
`;

export const ABOUT_TEXT_4 = `Happy researching!`;

// // // // //
// STATISTICS PAGE
// Text used in the statistics page.
// // // // //
export const STATISTICS_TITLE = `Statistics`;
export const STATISTICS_LIBRARY_FILTER = `Library`;
export const STATISTICS_LIBRARY_ALL = `All libraries`;
export const STATISTICS_LIBRARY_FILTER_HELP = `Leave this on all libraries to see every chart. Choose one library to limit every number and chart to that library.`;
export const STATISTICS_SUBTITLE = `View the system statistics.
The data is updated every minute, and shows the number of database elements in the system.`;

// // // // //
// LIBRARIES PAGE
// Text used in the libraries page.
// // // // //
export const LIBRARIES_TITLE = `Libraries`;
export const LIBRARIES_SUBTITLE = `Create, view, and edit libraries.
Libraries are the main way to organize and add resources. Every library is related to multiple resources.`;
export const NO_LIBRARIES_FOUND = `No libraries found! Add a new library to get started.`;

// // // // //
// PLAYLISTS PAGE
// Text used in the playlists page.
// // // // //
export const PLAYLISTS_TITLE = `Playlists`;
export const PLAYLISTS_SUBTITLE = `View and manage playlists.
Playlists are automatically synced from YouTube channels and map resources to their playlist groupings.`;
export const NO_PLAYLISTS_FOUND = `No playlists found. Process a library to sync.`;

// // // // //
// RESOURCES PAGE
// Text used in the resources page.
// // // // //
export const RESOURCES_TITLE = `Resources`;
export const RESOURCES_SUBTITLE = `Create, view, and edit resources.
Resources are the main data objects in the system. Every resource is related to transcripts.`;
export const NO_RESOURCES_FOUND = `No resources found! Add a new resource to get started.`;

// // // // //
// TRANSCRIPTS PAGE
// Text used in the transcripts page.
// // // // //
export const TRANSCRIPTS_TITLE = `Transcripts`;
export const TRANSCRIPTS_SUBTITLE = `Create, view, and edit transcripts.
A transcript is a text representation of a resource. Every transcript is related to a resource.`;
export const NO_TRANSCRIPTS_FOUND = `No transcripts found! Add a new resource to get started.`;

export const TRANSCRIPT_VIEW_TITLE = `View transcript`;
export const TRANSCRIPT_VIEW_SUBTITLE = `View the formatted transcript and its quotes.
Toggle Original to compare against the ingest captions; quote selection stays on the cleaned text.`;
export const NO_TRANSCRIPT_FOUND = `This transcript could not be found!

This might be due to the transcript not being fully processed yet, the resource having been being deleted, or simply a typo in the URL.`;

export const TRANSCRIPT_EDIT_TITLE = `Edit transcript`;
export const TRANSCRIPT_EDIT_SUBTITLE = `Review and edit the cleaned transcript before approving it.

You are free to edit the text in case of any semantic or grammatical errors.
Toggle Original to compare against the ingest captions. Updating from the cleaned view recreates chunks without re-running LLM cleanup.`;

export const TRANSCRIPT_EDIT_VIEW_TITLE = `View previous transcript`;
export const TRANSCRIPT_EDIT_VIEW_SUBTITLE = `View the previous version of the formatted transcript.
This is either the initial formatted transcript, created during resource creation, or the most recent transcript which has been approved.`;

// // // // //
// QUOTES PAGE
// Text used in the quotes page.
// // // // //
export const QUOTES_TITLE = `Quotes`;
export const QUOTES_SUBTITLE = `View, and edit quotes.
A quote is a slice of text from a transcript. Every quote is related to a transcript.`;
export const NO_QUOTES_FOUND = `No quotes found! View some transcript to create quotes.`;

// // // // //
// AUTHORS PAGE
// Text used in the authors page.
// // // // //
export const AUTHORS_TITLE = `Authors`;
export const AUTHORS_SUBTITLE = `View, and edit authors.
An author is a person who is the author, or co-author, of a resource. Aliases and exclusion phrases control how names are matched in resource titles and descriptions.`;
export const NO_AUTHORS_FOUND = `No authors found! Create an author using the button above.`;

// // // // //
// TEXT TRANSLATIONS PAGE
// Text used in the text translations page.
// // // // //
export const TEXT_TRANSLATIONS_TITLE = `Text translations`;
export const TEXT_TRANSLATIONS_SUBTITLE = `View, and edit translations of transcripts.
Translations can be further used to create audio translations.`;
export const NO_TEXT_TRANSLATIONS_FOUND = `No text translations found! Create a translation request using the button above.`;

export const TEXT_TRANSLATION_VIEW_TITLE = `View text translation`;
export const TEXT_TRANSLATION_VIEW_SUBTITLE = `View the formatted text translation.
Toggle Original to compare against the English transcript.`;
export const NO_TEXT_TRANSLATION_FOUND = `This text translation could not be found!

This might be due to the text translation not being fully processed yet, the resource having been being deleted, or simply a typo in the URL.`;

// // // // //
// AUDIO TRANSLATIONS PAGE
// Text used in the audio translations page.
// // // // //
export const AUDIO_TRANSLATIONS_TITLE = `Audio translations`;
export const AUDIO_TRANSLATIONS_SUBTITLE = `View, and edit audio voiceovers of translations.`;
export const NO_AUDIO_TRANSLATIONS_FOUND = `No audio translations found! Create a translation request using the button above.`;

export const AUDIO_TRANSLATION_VIEW_TITLE = `Audio translation`;
export const AUDIO_TRANSLATION_VIEW_SUBTITLE = `Listen to the audio created from the text translation.`;
export const NO_AUDIO_TRANSLATION_FOUND = `This audio translation could not be found!

This might be due to the audio translation not being fully processed yet, the resource having been being deleted, or simply a typo in the URL.`;

// // // // //
// HIGHLIGHT REQUESTS PAGE
// Text used in the highlight requests page.
// // // // //
export const HIGHLIGHT_REQUESTS_TITLE = `Highlight requests`;
export const HIGHLIGHT_REQUESTS_SUBTITLE = `Create, view, and edit highlight requests.
Highlight requests are used by the system to create highlights from resources.`;
export const NO_HIGHLIGHT_REQUESTS_FOUND = `No highlight requests found! Add a new highlight request to get started.`;

// // // // //
// HIGHLIGHT REVIEW PAGE
// Text used in the highlight review page.
// // // // //
export const HIGHLIGHT_TITLE = `Highlights`;
export const HIGHLIGHT_SUBTITLE = `View all highlights and review them.
Click on "Review Highlights" to begin voting on highlights.

Highlights which have passed the review process are set as "Approved," and can be used to create clips.`;
export const NO_HIGHLIGHT_FOUND = `No highlights found! Add a new highlight request to get started.`;

export const HIGHLIGHT_REVIEW_TITLE = `Highlight review`;
export const HIGHLIGHT_REVIEW_SUBTITLE = `Review highlights.
Vote on highlight requests to approve, disapprove, or reject them.`;

export const NO_HIGHLIGHT_REVIEW_FOUND = `This highlight could not be found!

This might be due to the highlight not being fully processed yet, the resource having been being deleted, or simply a typo in the URL.`;

// // // // //
// USERS PAGE
// Text used in the users page.
// // // // //
export const USERS_TITLE = `Users`;
export const USERS_SUBTITLE = `View, approve and remove users.
Users are people who have access to the system, and can be assigned different roles.`;
export const NO_USERS_FOUND = `No users found!`;

// // // // //
// COOKIES PAGE
// Text used in the cookies page.
// // // // //
export const COOKIES_TITLE = `Cookies`;
export const COOKIES_SUBTITLE = `View, set and remove cookies.
Cookies might be required for authentication with external platforms.

By default, the cookie with the latest timestamp is used for authentication.
However, you can manaully set a specific cookie to be used for authentication.`;
export const NO_COOKIES_FOUND = `No cookies found!`;

// // // // //
// UNEXPECTED ERROR PAGE
// Text on the error page if some unexpected exception occurred.
// // // // //
export const UNEXPECTED_ERROR_DESCRIPTION = `Something went wrong!

Make sure you are logged in, or are authorized to access this page.
Otherwise, please let us know of the error.`;

export const UNEXPECTED_ERROR_BUTTON_1 = `Home`;
export const UNEXPECTED_ERROR_BUTTON_2 = `Sign in`;

// // // // //
// 404 NOT FOUND PAGE
// Text on the error page if some unkown URL was queried.
// // // // //
export const NOT_FOUND_DESCRIPTION = `This page doesn't exist.

Check the URL for any typos.`;

export const NOT_FOUND_BUTTON = `Home`;

// // // // //
// AUTH PAGES
// Text used in sign up or sign in pages.
// // // // //
export const LOGGED_OUT = `You have been logged out.`;
export const NEXT_TIME = `Until next time!`;
export const LOG_BACK_IN = `You can log back in anytime.`;

// // // // //
// ACCOUNT PAGE
// User info, how-tos, etc.
// // // // //
export const ACCOUNT_TITLE = `Account`;
export const ACCOUNT_SUBTITLE = `Welcome, <username>!
To log out, click the exit icon on the top right of the page.`;

export const ACCOUNT_TEXT_1 = `User ID: <id>
Role: <role>
Username: <username>
Email: <email>

Account data can be modified below.`;

export const USER_UPDATE_TITLE = `Edit account information`;
export const USER_UPDATE_SUBTITLE = `Leave fields empty to keep their current values.`;

// // // // //
// TOUR STEPS
// Descriptions for the tour steps.
// // // // //

// Search tour.
export const TOUR_SEARCH_1 = `This is the search page for the FRISCO system.

There are two seach modes: standard (keyword-based) and assistant (AI-powered) search.

Click the icon on the right-hand side of the search box to toggle between the two modes.`;

export const TOUR_SEARCH_2 = `The AI-assistant is a powerful tool which will try to answer your question based on the transcripts in the system.

It can summarize content, find timestamps where some topic was discussed, and redirect you to the relevant resources.`;

export const TOUR_SEARCH_3 = `To search, type in the search bar and press enter.

The system will show the results below.`;

export const TOUR_SEARCH_4 = `Clicking on the gear icon on the right-hand side of the search box will open up the advanced search options.

While these parameters are usually not needed to be modified, they can be used to fine-tune the search results.`;

// About tour.
export const TOUR_ABOUT_1 = `Welcome to FRISCO, The Ayn Rand Institute's Flexible Retrieval and Indexing System for Content Optimization.`;

export const TOUR_ABOUT_2 = `The icons on the top right corner include features like changing the color theme and logging out.

The question mark icon opens the tour guide for that page. Hover over the icons to see their descriptions.`;

export const TOUR_ABOUT_3 = `The drawer on the left side contains the main navigation menu. Click on the icons to navigate to different pages.

Hover over the menu links to see short page descriptions.`;

export const TOUR_ABOUT_4 = `Each page starts with a title and a subtitle, describing the main features of the page.`;

export const TOUR_ABOUT_5 = `Whenever the tour guide icon on the top right is enabled, click on it to start a short tour describing the selected page's features more in-depth.`;

export const TOUR_ABOUT_6 = `There are two main features of the system:

1. Searching and managing content - automatically querying, transcribing, and processing multimedia resources.

2. Highlight requests and reviews - creating highlight requests based on the resources, and reviewing them.`;

export const TOUR_ABOUT_7 = `There are various ways to search for content in the system:

In addition to the homepage search functionality, database pages (e.g. resources or transcripts) are also filterable and searchable.

Access them from the sidebar on the left side of the page.`;

export const TOUR_ABOUT_8 = `Feedback and bug reports are most welcome.

Happy researching!`;

// Statistics tour.
export const TOUR_STATISTICS_1 = `This is the statistics page, which shows the number of database elements in the system, updated every minute.

It is useful for monitoring the system's state and performance.`;

export const TOUR_STATISTICS_2 = `The statistics are divided into different sections, each showing the number of elements in a specific database table.`;

export const TOUR_STATISTICS_3 = `Most graphs are interactive, allowing you to hover over them to view more details about the data, or hide certain sections by clicking on the legend.`;

// Libraries tour.
export const TOUR_LIBRARIES_1 = `A library is a collection of resources.

Every library's collection of resources are periodically queried and automatically processed by the system.`;
export const TOUR_LIBRARIES_2 = `Libraries are the main way to organize and add resources.

In addition, individual resources can be manually added to the system via the "Resources" page.`;
export const TOUR_LIBRARIES_3 = `To create a new library, click on the "Add new library" button. 

Fill in the required fields and click "Save" to create the library.`;
export const TOUR_LIBRARIES_4 = `The table below shows the libraries already in the system, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.`;
export const TOUR_LIBRARIES_5 = `Select a library to edit it. Select one or multiple libraries to approve, retry, or delete them.

All approved libraries are "active" (in queue) and will be periodically queried for new resources.`;
export const TOUR_LIBRARIES_6 = `In case of any issues, view the the status and status message columns for more information.`;
export const TOUR_LIBRARIES_7 = `After approving one or more libraries, and starting the background process, the system will automatically query the latest content, creating multiple resources, which are then accessible via the "Resources" page.`;

// Playlists tour.
export const TOUR_PLAYLISTS_1 = `A playlist is a grouping of resources, automatically synced from YouTube channels during library processing.

Unlike libraries and resources, playlists do not need to be manually created or approved — they are discovered and kept in sync automatically whenever a library is processed.`;
export const TOUR_PLAYLISTS_2 = `Each playlist maps to an actual YouTube playlist from a channel. Resources within a playlist maintain their original ordering (position).

When new videos are added to a YouTube playlist, they will appear here after the next library sync.`;
export const TOUR_PLAYLISTS_3 = `The table shows the playlist name, description, parent library, URL, and timestamps.

Click the URL icon to open the original YouTube playlist. Use the search bar to filter by name, description, or URL.`;
export const TOUR_PLAYLISTS_4 = `Select one or more playlists to delete them. Deleting a playlist only removes the grouping — the underlying resources are not affected.

Playlists are also automatically deleted if their parent library is deleted.`;

// Resources tour.
export const TOUR_RESOURCES_1 = `A resource is the main data object in the system. 

It usually contains a title, description, and most importantly, a link to the media source.`;
export const TOUR_RESOURCES_2 = `Every processed resource will be linked to a transcript, which is a representation of the media source. 

Transcripts are then used to create quotes and perforom detailed searches.`;
export const TOUR_RESOURCES_3 = `To create a new resource, click on the "Add new resource" button. 

Fill in the required fields and click "Save" to create the resource.`;
export const TOUR_RESOURCES_4 = `The table below shows the resources already in the system, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.`;
export const TOUR_RESOURCES_5 = `Select a resource to view or edit it. Select one or multiple resources to approve, retry, or delete them.`;
export const TOUR_RESOURCES_6 = `In case of any issues, view the the status and status message columns for more information.`;
export const TOUR_RESOURCES_7 = `After approving a resource, the system will automatically process it, creating a transcript, accessible via the "Transcripts" page.`;

// Transcripts tour.
export const TOUR_TRANSCRIPTS_1 = `A transcript is a text representation of a resource.

It contains the resource transcript, both the raw metadata and an automatically formatted and punctuated version.`;
export const TOUR_TRANSCRIPTS_2 = `A new transcript is automatically created by the system whenever a resource is approved and successfully processed.`;
export const TOUR_TRANSCRIPTS_3 = `The table below shows the transcripts already in the system, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.`;
export const TOUR_TRANSCRIPTS_4 = `Select a transcript to edit it. Select one or multiple transcripts to approve, retry, or delete them.`;
export const TOUR_TRANSCRIPTS_5 = `View the formatted transcript by clicking on the "View" button at the end of the table row. This will also show any quotes.`;
export const TOUR_TRANSCRIPTS_6 = `In case of any issues, view the the status and status message columns for more information.`;
export const TOUR_TRANSCRIPTS_7 = `After approving a transcript, the system will automatically process it, creating a formatted transcript usable for quotes, and vector embedding chunks, usable for AI-powered search.`;

// Quotes tour.
export const TOUR_QUOTES_1 = `A quote is a slice of text from a transcript.

It is used to highlight important parts of the transcript.`;
export const TOUR_QUOTES_2 = `Quotes are created from transcripts, and are linked to them.`;
export const TOUR_QUOTES_3 = `The table below shows the quotes already in the system, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.`;
export const TOUR_QUOTES_4 = `View the quote by clicking on the "View" button at the end of the table row. Select one or multiple quotes to delete them.`;

// Authors tour.
export const TOUR_AUTHORS_1 = `An author is a person who is the author, or co-author, of a resource.

Authors are linked to resources.`;
export const TOUR_AUTHORS_2 = `The table below shows the authors already in the system, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.`;
export const TOUR_AUTHORS_3 = `Select an author to edit it. Select one or multiple authors to delete them, or to apply aliases and exclusion phrases to resources.`;

// Text translations tour.
export const TOUR_TEXT_TRANSLATIONS_1 = `A text translation is a translated version of a transcript.

It can further be used to create audio translations, which is a voiceover for the translated transcript.`;

export const TOUR_TEXT_TRANSLATIONS_2 = `To create a new text translation, click on the "Add new text translation" button.

Fill in the required fields, including the language and the transcript to translate, and click "Save" to create the translation.`;

export const TOUR_TEXT_TRANSLATIONS_3 = `The table below shows the text translations already in the system, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.

Select a text translation to edit it. Select one or multiple text translations to delete them.`;

// Audio translations tour.
export const TOUR_AUDIO_TRANSLATIONS_1 = `An audio translation is a voiceover for a text translation.
It is created from a text translation, and is linked to it.`;

export const TOUR_AUDIO_TRANSLATIONS_2 = `To create a new audio translation, click on the "Add new audio translation" button.

Fill in the required fields, including the text translation to create the audio translation for, and click "Save" to create the audio translation.`;

export const TOUR_AUDIO_TRANSLATIONS_3 = `The table below shows the audio translations already in the system, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.

Select an audio translation to edit it. Select one or multiple audio translations to delete them.`;

// Highlight requests tour.
export const TOUR_HIGHLIGHT_REQUESTS_1 = `The FRISCO system automatically creates potential highlights from resources, based on the user's highlight requests.

Created highlights are then voted on by level 1 and 2 reviewers, and are visible on the "View and review" page.`;
export const TOUR_HIGHLIGHT_REQUESTS_2 = `To create a new highlight request, click on the "New highlight request" button.

Fill in the required fields, including optional filters like library, author, or maximum clip length constraints.

Then, click "Apply" to create the highlight request.`;
export const TOUR_HIGHLIGHT_REQUESTS_3 = `Existing highlight requests are shown in the table below, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.

Select a highlight request to edit it. Select one or multiple highlight requests to approve, retry, or delete them.`;

// Highlight review tour.
export const TOUR_HIGHLIGHT_REVIEWS_1 = `Highlights are created from resources based on the user's highlight requests.

This page is used to both view and review those highlights.

The voting process is done by level 1 and 2 reviewers, voting "yes", "no", or "maybe" on each highlight request.`;
export const TOUR_HIGHLIGHT_REVIEWS_2 = `To begin the review process, click on the "Review Highlights" button.

This will show the highlights that require the reviewer's attention, allowing them to vote on each highlight alongside adding an optional comment.`;
export const TOUR_HIGHLIGHT_REVIEWS_3 = `Existing highlights are shown in the table below, allowing filtering and sorting by different columns, or performing a quick search via the search bar on the right hand side.

The status column shows the current status of the highlight:
- Awaiting approval: waiting for reviews.
- Approved: will be used to create clips.
- Completed: has been created as a clip.
- Rejected: will not be used to create clips.`;
